import {InjectionToken} from '@angular/core';
import {MavenSetupInputs} from '../maven-domain';
import {MavenCardDto} from './maven-operations';

// THE MAVEN RUN SEAM (CR-20260910-001 milestone 4, lane S6).
//
// One interface, two implementations behind one token: the real backend client, and a stub that
// answers from memory. The stub is what lets this lane build and gate the WHOLE panel before
// milestone 3's engine exists — the DTOs are frozen, so a stub built to them is not a guess about
// the delivery, it is the delivery's shape.
//
// WHY A STAGE CALLBACK RATHER THAN A PLAIN PROMISE. Maven pushes several MavenRunStageDto while
// it works and exactly ONE MavenResultReadyDto at the end (guarantee G6). A bare promise can
// carry the delivery but not the stages, and the stages are the whole of what the trader looks at
// while he waits. So: `onStage` fires zero or more times, then the promise settles ONCE.
//
// THERE IS NO CANCEL AND NO STOP, and that is a ruling (R-D), not an omission. Pressing Apply
// again supersedes the run in flight: the client sends a new run, and the backend pushes
// 'superseded' to the displaced one exactly once. Do not add a cancel method to this interface —
// there is no command on the other end of it.

export interface MavenRunRequest {
    // Supersede scope, with the terminal. One Maven panel instance = one run at a time.
    panelInstanceId: string;
    // The trader's Setup, in DOMAIN shapes (JS Dates, normalized-or-raw money). The mapping to
    // the wire — calendar strings from LOCAL date parts, money in plain dollars — happens in ONE
    // place, inside the client, so no caller can get the timezone rule wrong.
    setup: MavenSetupInputs;
    // The portfolio's default quantity, used HERE to convert a normalized money entry to plain
    // dollars before it crosses, exactly as the ORION (OPG) does. It does not itself cross.
    defaultQty: number;
}

// What the panel gets back — A DISCRIMINATED UNION, and the shape is the point.
//
// ⚠ LEADER RULING L46. Resolving instead of rejecting was accepted on ONE CONDITION: "the client
// returns a discriminated result whose outcome the caller MUST inspect — never a nullable or
// empty cards array". The reasoning is the failure it prevents: if "resolves" quietly becomes
// "returns nothing", a `terminalFailed` renders as an EMPTY PANEL, which under R-C's no-messages
// rule is exactly the silent failure that is hardest to see. The divergence buys inline refusals;
// it must not buy silence.
//
// A flat object with `outcome: string` and `cards: []` would satisfy the letter and lose the
// point — a caller could read `.cards`, find none, and draw an empty column for a run that
// FAILED. So `cards` EXISTS ONLY on the succeeded variant: reading it without first narrowing on
// `outcome` does not compile. The compiler, not a convention, is what makes the outcome
// impossible to ignore.
export type MavenRunResult =
    | MavenRunSucceeded
    | MavenRunEmpty
    | MavenRunSuperseded
    | MavenRunRefused
    | MavenRunFailed;

export interface MavenRunSucceeded {
    outcome: 'succeeded';
    // EVERY package the run produced, in the run's own rank order. Two or more, never one
    // (guarantee G1). The "show top N" setting is a DISPLAY limit the results column applies when
    // it renders (ruling R-B) — never a cut here, and never a re-sort: what the backend ranked
    // first stays first (guarantee G8).
    cards: MavenCardDto[];

    /**
     * "single" | "double", straight off the delivery (ruling L39). On the SUCCEEDED arm only — a run
     * that produced no package has no portfolio fact to state, and a refusal never reached one.
     */
    strategyType: string;
    // The portfolio's default quantity the normalized figures on every card refer to.
    defaultQty: number;

    // CR-20260914-001, the scoped exception to R-C: the dates on which a side could not be hedged
    // to the expected range, one worded line each, as delivered. Empty when there is nothing to say.
    expectedRangeNotes: string[];
}

// A run that finished and produced no package at all. It carries ONE SENTENCE — the single
// exception to R-C's no-messages rule, because a blank panel reads as a failure — and, since
// CR-20260914-001, the expected-range lines too: when EVERY day fell out for that one cause, the
// sentence alone would leave the trader guessing.
export interface MavenRunEmpty {
    outcome: 'empty';
    sentence: string;
    expectedRangeNotes: string[];
}

// Displaced by a newer Apply from the same panel. Carries nothing: there is nothing to show, and
// a newer run already owns the column.
export interface MavenRunSuperseded {
    outcome: 'superseded';
}

// Refused before anything was ordered. `code` is from the contract's CLOSED list; the wording
// lives on the client (ruling Q-M5) and is resolved by maven-refusal-messages.
export interface MavenRunRefused {
    outcome: 'preSendBlocked';
    code: string;
    detail: string | null;
}

// The run reached the backend and did not finish.
export interface MavenRunFailed {
    outcome: 'terminalFailed';
    detail: string;
}

export interface MavenStageEvent {
    stage: string;
    detail: string | null;
}

export interface IMavenRunSkill {
    // Resolves ONCE, for every outcome including the failures — the panel renders a refusal in
    // place rather than catching an exception. `onStage` fires zero or more times before that.
    run(request: MavenRunRequest, onStage: (event: MavenStageEvent) => void): Promise<MavenRunResult>;
}

export const MAVEN_RUN_SKILL = new InjectionToken<IMavenRunSkill>('MAVEN_RUN_SKILL');
